import { createApiClient } from './apiClient';
import {
  StatusResponse,
  Event,
  Insight,
  HeatmapCell,
  GovernanceCallout,
  ExportResult,
  PeerBenchmark,
  DrilldownLink,
  CompliancePack,
} from '../types/executiveDashboard';
import {
  StatusResponseSchema,
  EventsResponseSchema,
  InsightsResponseSchema,
  HeatmapResponseSchema,
  CalloutsResponseSchema,
  ExportsResponseSchema,
  PeerBenchmarksResponseSchema,
  DrilldownLinksResponseSchema,
  CompliancePacksResponseSchema,
  safeValidate,
} from '../validation';
import type { AxiosResponse, AxiosError } from 'axios';

// Mock data for fallbacks when API calls fail
const mockData = {
  status: {
    last_update: new Date().toISOString(),
    peer_count: 15,
    system_health: 'healthy',
    tenant_id: 'default-tenant',
    update_frequency_minutes: 60
  } as StatusResponse,
  
  events: [
    { 
      id: 'evt-001', 
      title: 'Critical Vulnerability Detected', 
      timestamp: '2025-08-10T14:30:00Z', 
      severity: 'critical', 
      category: 'security',
      description: 'Critical vulnerability detected in web application firewall',
      source: 'WAF',
      tenant_id: 'default-tenant'
    },
    { 
      id: 'evt-002', 
      title: 'Unusual Network Activity', 
      timestamp: '2025-08-10T12:15:00Z', 
      severity: 'high', 
      category: 'network',
      description: 'Unusual outbound traffic detected from internal servers',
      source: 'IDS',
      tenant_id: 'default-tenant'
    },
    { 
      id: 'evt-003', 
      title: 'Failed Login Attempts', 
      timestamp: '2025-08-10T10:45:00Z', 
      severity: 'medium', 
      category: 'access',
      description: 'Multiple failed login attempts from external IP addresses',
      source: 'IAM',
      tenant_id: 'default-tenant'
    }
  ] as Event[],
  
  insights: [
    { 
      id: 'ins-001', 
      title: 'Ransomware Risk Increasing', 
      description: 'Recent threat intelligence indicates increased ransomware activity targeting your industry.', 
      category: 'threat', 
      impact_level: 'high',
      recommended_action: 'Review backup systems and implement additional security controls',
      tenant_id: 'default-tenant'
    },
    { 
      id: 'ins-002', 
      title: 'Compliance Gap Detected', 
      description: 'New PCI DSS requirements not fully implemented in payment processing systems.', 
      category: 'compliance', 
      impact_level: 'medium',
      recommended_action: 'Schedule compliance review meeting with security team',
      due_date: '2025-09-15T00:00:00Z',
      tenant_id: 'default-tenant'
    }
  ] as Insight[],
  
  heatmap: [
    { 
      business_unit: 'Finance', 
      risk_score: 85, 
      trend: 'increasing', 
      alert_count: 42,
      risk_level: 'high'
    },
    { 
      business_unit: 'Operations', 
      risk_score: 65, 
      trend: 'stable', 
      alert_count: 28,
      risk_level: 'medium'
    },
    { 
      business_unit: 'IT', 
      risk_score: 78, 
      trend: 'decreasing', 
      alert_count: 36,
      risk_level: 'high'
    },
    { 
      business_unit: 'Sales', 
      risk_score: 45, 
      trend: 'stable', 
      alert_count: 15,
      risk_level: 'medium'
    }
  ] as HeatmapCell[],
  
  callouts: [
    { 
      id: 'call-001', 
      title: 'Board Attention Required', 
      description: 'Critical security vulnerabilities require immediate board attention', 
      due_date: '2025-08-20T00:00:00Z',
      priority: 'high',
      status: 'pending',
      owner: 'CISO',
      tenant_id: 'default-tenant'
    },
    { 
      id: 'call-002', 
      title: 'Regulatory Deadline Approaching', 
      description: 'GDPR compliance documentation due in 15 days', 
      due_date: '2025-08-25T00:00:00Z',
      priority: 'medium',
      status: 'in_progress',
      owner: 'Compliance Officer',
      tenant_id: 'default-tenant'
    }
  ] as GovernanceCallout[],
  
  exportPacket: [
    { 
      id: 'exp-001', 
      format: 'PDF', 
      url: '/exports/executive-summary.pdf'
    },
    { 
      id: 'exp-002', 
      format: 'PPTX', 
      url: '/exports/risk-report.pptx'
    }
  ] as ExportResult[],
  
  peerBenchmark: [
    { 
      businessUnit: 'Overall Security Posture', 
      score: 78
    },
    { 
      businessUnit: 'Incident Response Time', 
      score: 82
    }
  ] as PeerBenchmark[],
  
  drilldownLinks: [
    { 
      label: 'Vulnerability Management Dashboard', 
      url: '/security/vulnerabilities'
    },
    { 
      label: 'Compliance Status Report', 
      url: '/compliance/status'
    }
  ] as DrilldownLink[],
  
  compliancePacks: [
    { 
      id: 'pack-001', 
      name: 'PCI DSS Compliance Pack', 
      url: '/compliance/pci-dss-pack.zip'
    },
    { 
      id: 'pack-002', 
      name: 'HIPAA Compliance Pack', 
      url: '/compliance/hipaa-pack.zip'
    }
  ] as CompliancePack[]
};

/**
 * Get API client for alerts API
 * Uses centralized configuration from apiClient.ts
 */
const getAlertsApiClient = () => {
  return createApiClient();
};

// Alerts API Service for Executive Dashboard
export const alertsApi = {
  // Get status information for a specific tenant
  getStatus: (tenant_id: string): Promise<StatusResponse> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/status/`)
      .then((response: AxiosResponse): StatusResponse => {
        const validated = safeValidate(StatusResponseSchema, response.data);
        return (validated ?? mockData.status) as StatusResponse;
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching status data:', error);
        return mockData.status;
      });
  },

  // Get events for a specific tenant
  getEvents: (tenant_id: string): Promise<Event[]> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/events/`)
      .then((response: AxiosResponse): Event[] => {
        const validated = safeValidate(EventsResponseSchema, response.data);
        return (validated ?? mockData.events) as Event[];
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching events data:', error);
        return mockData.events;
      });
  },

  // Get insights for a specific tenant
  getInsights: (tenant_id: string): Promise<Insight[]> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/insights/`)
      .then((response: AxiosResponse): Insight[] => {
        const validated = safeValidate(InsightsResponseSchema, response.data);
        return (validated ?? mockData.insights) as Insight[];
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching insights data:', error);
        return mockData.insights;
      });
  },

  // Get heatmap data for a specific tenant
  getHeatmap: (tenant_id: string): Promise<HeatmapCell[]> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/heatmap/`)
      .then((response: AxiosResponse): HeatmapCell[] => {
        const validated = safeValidate(HeatmapResponseSchema, response.data);
        return (validated ?? mockData.heatmap) as HeatmapCell[];
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching heatmap data:', error);
        return mockData.heatmap;
      });
  },

  // Get governance callouts for a specific tenant
  getCallouts: (tenant_id: string): Promise<GovernanceCallout[]> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/callouts/`)
      .then((response: AxiosResponse): GovernanceCallout[] => {
        const validated = safeValidate(CalloutsResponseSchema, response.data);
        return (validated ?? mockData.callouts) as GovernanceCallout[];
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching callouts data:', error);
        return mockData.callouts;
      });
  },

  // Get board packet export options for a specific tenant
  getExportPacket: (tenant_id: string): Promise<ExportResult[]> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/export-packet/`)
      .then((response: AxiosResponse): ExportResult[] => {
        const validated = safeValidate(ExportsResponseSchema, response.data);
        return (validated ?? mockData.exportPacket) as ExportResult[];
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching export packet data:', error);
        return mockData.exportPacket;
      });
  },

  // Get peer benchmark data for a specific tenant
  getPeerBenchmark: (tenant_id: string): Promise<PeerBenchmark[]> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/peer-benchmark/`)
      .then((response: AxiosResponse): PeerBenchmark[] => {
        const validated = safeValidate(PeerBenchmarksResponseSchema, response.data);
        return (validated ?? mockData.peerBenchmark) as PeerBenchmark[];
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching peer benchmark data:', error);
        return mockData.peerBenchmark;
      });
  },

  // Get drill-down links for a specific tenant
  getDrilldownLinks: (tenant_id: string): Promise<DrilldownLink[]> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/drilldown-links/`)
      .then((response: AxiosResponse): DrilldownLink[] => {
        const validated = safeValidate(DrilldownLinksResponseSchema, response.data);
        return (validated ?? mockData.drilldownLinks) as DrilldownLink[];
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching drilldown links data:', error);
        return mockData.drilldownLinks;
      });
  },

  // Get compliance packs for a specific tenant
  getCompliancePacks: (tenant_id: string): Promise<CompliancePack[]> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/compliance-packs/`)
      .then((response: AxiosResponse): CompliancePack[] => {
        const validated = safeValidate(CompliancePacksResponseSchema, response.data);
        return (validated ?? mockData.compliancePacks) as CompliancePack[];
      })
      .catch((error: AxiosError) => {
        console.error('Error fetching compliance packs data:', error);
        return mockData.compliancePacks;
      });
  },

  // === COMPREHENSIVE BOARD INTEGRATION ENDPOINTS ===

  // Core Alert Enrichment
  enrichAlert: (tenant_id: string, alertIdOrData: string | { alertId: string; enrichmentType?: string; parameters?: Record<string, unknown> }): Promise<unknown> => {
    const client = getAlertsApiClient();
    
    // Handle both simple and advanced usage patterns
    if (typeof alertIdOrData === 'string') {
      // Simple usage: enrichAlert(tenant_id, alertId)
      return client.post(`/alerts/${alertIdOrData}/enrich`).then((response: AxiosResponse) => response.data);
    } else {
      // Advanced usage: enrichAlert(tenant_id, { alertId, enrichmentType, parameters })
      const { alertId, enrichmentType, parameters } = alertIdOrData;
      const payload = { enrichmentType, parameters };
      return client.post(`/alerts/${alertId}/enrich`, payload).then((response: AxiosResponse) => response.data);
    }
  },

  getAlertEnrichment: (tenant_id: string, alertId: string, includeDetails: boolean = false): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get(`/api/alerts/${alertId}/enrichment?include_details=${includeDetails}`).then((response: AxiosResponse) => response.data);
  },

  // Executive Dashboard Integration
  getExecutiveEnrichmentSummary: (tenant_id: string): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/executive-dashboard`).then((response: AxiosResponse) => response.data.dashboard);
  },

  // Analyst Dashboard Integration
  getAnalystEnrichmentQueue: (tenant_id: string): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/analyst-dashboard`).then((response: AxiosResponse) => response.data);
  },

  // Integration Hub Integration
  getEnrichmentConnectorHealth: (tenant_id: string): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/enrichment/connector-health`).then((response: AxiosResponse) => response.data);
  },

  // Threat Intelligence Integration
  getThreatCorrelations: (tenant_id: string): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get('/alerts/enrichment/threat-correlations').then((response: AxiosResponse) => response.data);
  },

  // Compliance Integration
  getComplianceImpact: (tenant_id: string): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/compliance`).then((response: AxiosResponse) => response.data);
  },

  // Advanced Features Integration
  getEnrichmentKPIs: (_tenant_id: string, days: number = 7): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/enrichment/kpis?days=${days}`).then((response: AxiosResponse) => response.data);
  },

  // Rules Management Integration
  getRulesAnalysis: (tenant_id: string): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/rules-management`).then((response: AxiosResponse) => response.data);
  },

  // Data Governance Integration
  getEnrichmentGovernance: (tenant_id: string): Promise<unknown> => {
    const client = getAlertsApiClient();
    return client.get(`/alerts/${tenant_id}/threat-intel`).then((response: AxiosResponse) => response.data);
  },

  // Analyst Feedback Submission
  submitFeedback: (payload: {
    alert_id: string;
    feedback_type: string;
    confidence_score: number;
    impact_assessment: string;
    tags: string[];
    remediation_steps: string[];
    mitre_techniques: Array<{
      technique_id: string;
      name: string;
      confidence: number;
      category: string;
    }>;
    notes: string;
    tenant_id?: string;  // P1-3: Optional - backend extracts from auth token
    version?: number;  // FIX #5: Alert version for concurrent feedback conflict detection
    // IMMEDIATE ACTIONS
    immediate_actions?: {
      create_incident: boolean;
      trigger_playbook: boolean;
      send_notifications: boolean;
    };
  }): Promise<{ success: boolean; error?: string }> => {
    const client = getAlertsApiClient();
    
    // FIX #6: Generate unique idempotency key for preventing duplicate ITSM tickets
    const idempotencyKey = `alert-${payload.alert_id}-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    
    // FIX #5 & #6: Add version and idempotency-key headers
    const headers: Record<string, string> = {
      'X-Idempotency-Key': idempotencyKey
    };
    
    return client.post(`/api/alerts/${payload.alert_id}/feedback`, payload, { headers })
      .then((response: AxiosResponse) => response.data as { success: boolean })
      .catch((error: AxiosError) => {
        // FIX #6: Re-throw error to preserve HTTP status for proper error handling
        // This allows the calling code to handle 409 (conflict) and other status codes
        throw error;
      });
  },

  // P0.13 Phase 1: Get processing queue count
  getProcessingQueue: (_tenant_id: string): Promise<{
    pending_enrichment: number;
    in_progress: number;
    total_processing: number;
    critical_pending: number;
    high_pending: number;
  }> => {
    const client = getAlertsApiClient();
    return client.get(`/api/alerts/processing-queue`)
      .then((response: AxiosResponse) => response.data)
      .catch((error) => {
        console.error('Error fetching processing queue:', error);
        return {
          pending_enrichment: 0,
          in_progress: 0,
          total_processing: 0,
          critical_pending: 0,
          high_pending: 0
        };
      });
  },

  // Multi-Analyst Workflow: List alerts with filters
  listAlerts: (params?: {
    page?: number;
    page_size?: number;
    severity?: string;
    status?: string;
    assigned_to?: string;
  }): Promise<{ alerts: any[]; total: number; page: number; page_size: number }> => {
    const client = getAlertsApiClient();
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.severity) queryParams.append('severity', params.severity);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.assigned_to) queryParams.append('assigned_to', params.assigned_to);
    
    // P0-3: CRITICAL - Must include /api prefix (following API prefix consistency rule)
    const url = `/api/alerts${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    return client.get(url)
      .then((response: AxiosResponse) => response.data)
      .catch((error: AxiosError) => {
        console.error('Error listing alerts:', error);
        return { alerts: [], total: 0, page: 1, page_size: 50 };
      });
  },

  // Multi-Analyst Workflow: Claim alert
  // T8: Added AbortSignal support for request cancellation
  claimAlert: (alertId: string, signal?: AbortSignal): Promise<{ success: boolean; message: string; alert: { id: string; status: string; assigned_to: string; picked_by: string; picked_at: string; version: number } }> => {
    const client = getAlertsApiClient();
    return client.post(`/api/alerts/${alertId}/claim`, {}, { signal })
      .then((response: AxiosResponse) => response.data)
      .catch((error: AxiosError) => {
        // R72: Wrap console.error in dev check
        if (process.env.NODE_ENV === 'development') {
          console.error('Error claiming alert:', error);
        }
        throw error;
      });
  },

  // Multi-Analyst Workflow: Release alert
  releaseAlert: (alertId: string, reason?: string): Promise<{ success: boolean; message: string }> => {
    const client = getAlertsApiClient();
    return client.post(`/api/alerts/${alertId}/release`, { reason })
      .then((response: AxiosResponse) => response.data)
      .catch((error: AxiosError) => {
        console.error('Error releasing alert:', error);
        throw error;
      });
  },

  // Multi-Analyst Workflow: Assign alert to user
  assignAlert: (alertId: string, userId: string): Promise<{ success: boolean; message: string }> => {
    const client = getAlertsApiClient();
    return client.post(`/api/alerts/${alertId}/assign`, { user_id: userId })
      .then((response: AxiosResponse) => response.data)
      .catch((error: AxiosError) => {
        console.error('Error assigning alert:', error);
        throw error;
      });
  },

  // Multi-Analyst Workflow: Resolve alert
  resolveAlert: (alertId: string, resolutionNotes: string, resolutionAction?: string): Promise<{ success: boolean; message: string }> => {
    const client = getAlertsApiClient();
    return client.post(`/api/alerts/${alertId}/resolve`, { 
      resolution_notes: resolutionNotes,
      resolution_action: resolutionAction
    })
      .then((response: AxiosResponse) => response.data)
      .catch((error: AxiosError) => {
        console.error('Error resolving alert:', error);
        throw error;
      });
  },

  // Multi-Analyst Workflow: Get alert timeline
  getAlertTimeline: (alertId: string): Promise<{ alert_id: string; timeline: any[]; total_events: number }> => {
    const client = getAlertsApiClient();
    return client.get(`/api/alerts/${alertId}/timeline`)
      .then((response: AxiosResponse) => response.data)
      .catch((error: AxiosError) => {
        console.error('Error fetching alert timeline:', error);
        return { alert_id: alertId, timeline: [], total_events: 0 };
      });
  }
};

export default alertsApi;
